/**
 * Multi-site — Super Admin's Site Management page.
 *
 * Surface:
 *   • List existing sites (Salem default + any provisioned new sites).
 *   • Create-new-site dialog: site_code (3 chars), site_name, location,
 *     code_prefix (2-5 chars), optional initial_admin_email/name.
 *   • Toggle site status (active / inactive). Salem cannot be inactivated.
 *
 * Phases now live (as of P3, Feb 2026):
 *   • P1 — sites_registry + provisioning (this page).
 *   • P2 — request-level DB routing via X-Site-Id (every operator page is site-aware).
 *   • P3 — bundle/document codes are auto-prefixed with the site's code_prefix.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  ArrowLeft, Plus, Loader2, Building2, Database, MapPin, Hash, ShieldCheck,
  ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Circle, ExternalLink,
} from 'lucide-react';
import SiteAdminGrants from './SiteAdminGrants';
import DispatchSectionsPanel from './DispatchSectionsPanel';

export default function SiteManagement() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState({});           // site_id -> bool
  const [bootstrap, setBootstrap] = useState({});         // site_id -> response
  const [bootstrapLoading, setBootstrapLoading] = useState({});  // site_id -> bool
  const [form, setForm] = useState({
    site_code: '', site_name: '', location: '', code_prefix: '',
    initial_admin_email: '', initial_admin_name: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/sites-registry/admin');
      setRows(r.data?.sites || []);
      setScope(r.data?.scope || 'all');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sites (super admin or site admin required)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadBootstrap = async (siteId) => {
    setBootstrapLoading((s) => ({ ...s, [siteId]: true }));
    try {
      const r = await apiClient.get(`/sites-registry/admin/${siteId}/bootstrap-status`);
      setBootstrap((s) => ({ ...s, [siteId]: r.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load bootstrap for ${siteId}`);
    } finally {
      setBootstrapLoading((s) => ({ ...s, [siteId]: false }));
    }
  };

  const toggleExpand = (siteId) => {
    const willOpen = !expanded[siteId];
    setExpanded((s) => ({ ...s, [siteId]: willOpen }));
    if (willOpen && !bootstrap[siteId]) {
      loadBootstrap(siteId);
    }
  };

  const onCreate = async () => {
    if (!form.site_code.trim() || !form.site_name.trim() || !form.code_prefix.trim()) {
      toast.error('Site code, site name and code prefix are required');
      return;
    }
    setCreating(true);
    try {
      await apiClient.post('/sites-registry/admin', {
        site_code: form.site_code.trim().toUpperCase(),
        site_name: form.site_name.trim(),
        location: form.location.trim() || null,
        code_prefix: form.code_prefix.trim().toUpperCase(),
        initial_admin_email: form.initial_admin_email.trim() || null,
        initial_admin_name: form.initial_admin_name.trim() || null,
      });
      toast.success('Site created — DB provisioning is running in the background. Refresh in 30–60s to see "ready" status.');
      setShowCreate(false);
      setForm({ site_code: '', site_name: '', location: '', code_prefix: '',
                initial_admin_email: '', initial_admin_name: '' });
      load();
    } catch (e) {
      // 2026-05-14 — Render the real reason. Backend raises HTTPException
      // with `detail: <string>` for every known failure; Pydantic 422
      // validation errors come back as `detail: [{loc, msg, …}]` (array),
      // which the old toast rendered as the fallback "Failed to create
      // site" because `||` accepted the truthy array. Coerce to a
      // readable string here so SuperAdmins can actually act on the
      // error. Network/CORS failures (no response) also get a clearer
      // hint than the previous opaque toast.
      let msg;
      if (!e.response) {
        msg = `Network error or backend timeout while creating site (${e.message || 'no response'}). The DB may still be provisioning in the background — refresh in 60s.`;
      } else {
        const d = e.response?.data?.detail;
        if (Array.isArray(d)) {
          msg = d.map((x) => `${(x.loc || []).join('.')}: ${x.msg}`).join('; ');
        } else if (typeof d === 'string') {
          msg = d;
        } else if (d && typeof d === 'object') {
          msg = JSON.stringify(d);
        } else {
          msg = `Backend returned ${e.response.status} with no detail`;
        }
      }
      toast.error(msg, { duration: 10000 });
    } finally {
      setCreating(false);
    }
  };

  const onToggleStatus = async (site) => {
    if (site.is_default) {
      toast.error('Salem (default) cannot be deactivated');
      return;
    }
    const newStatus = site.status === 'active' ? 'inactive' : 'active';
    try {
      await apiClient.patch(`/sites-registry/admin/${site.site_id}`, { status: newStatus });
      toast.success(`Site ${site.site_name} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update site');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="site-management-page">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <Building2 className="h-5 w-5" /> Site Management
              {scope === 'all'
                ? <Badge className="ml-2 bg-purple-100 text-purple-700 border-purple-200">Super Admin</Badge>
                : <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200">Site Admin</Badge>}
            </h1>
            <p className="text-xs text-stone-500 mt-1">
              Multi-site control. Each site has its own MongoDB database starting with 0 records.
              Salem is the implicit default. (Phase 1 — data routing wires up in Phase 2.)
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-site-btn"
            disabled={scope !== 'all'}
            title={scope !== 'all' ? 'Only Super Admins can create new sites' : ''}
          >
            <Plus className="h-4 w-4 mr-1" /> New Site
          </Button>
        </div>

        <Card className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-stone-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-stone-500">No sites. Salem should auto-seed on next backend boot.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600 text-xs">
                <tr>
                  <th className="text-left p-3">Site</th>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Code Prefix</th>
                  <th className="text-left p-3">Database</th>
                  <th className="text-left p-3">Location</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Created</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <React.Fragment key={s.site_id}>
                  <tr className="border-t border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`site-row-${s.site_id}`}>
                    <td className="p-3">
                      <button
                        onClick={() => toggleExpand(s.site_id)}
                        className="flex items-start gap-1 text-left w-full"
                        data-testid={`expand-${s.site_id}`}
                      >
                        {expanded[s.site_id]
                          ? <ChevronDown className="h-4 w-4 mt-0.5 text-stone-500" />
                          : <ChevronRight className="h-4 w-4 mt-0.5 text-stone-500" />}
                        <div>
                          <div className="font-semibold text-stone-900">
                            {s.site_name}
                            {s.is_default && <span className="ml-2 text-[10px] text-stone-500 font-normal">DEFAULT</span>}
                          </div>
                          <div className="text-xs font-mono text-stone-500">{s.site_id}</div>
                        </div>
                      </button>
                    </td>
                    <td className="p-3 font-mono text-emerald-700">{s.site_code}</td>
                    <td className="p-3 font-mono">
                      {s.code_prefix ? <Badge variant="outline">{s.code_prefix}-</Badge> : <span className="text-stone-400 text-xs">— (no prefix)</span>}
                    </td>
                    <td className="p-3 font-mono text-xs text-stone-600">
                      <span className="inline-flex items-center gap-1"><Database className="h-3 w-3" /> {s.db_name}</span>
                    </td>
                    <td className="p-3 text-stone-600">{s.location || '—'}</td>
                    <td className="p-3">
                      <Badge
                        className={
                          s.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-stone-200 text-stone-600 border-stone-300'
                        }
                      >
                        {s.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-stone-500">{(s.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="p-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onToggleStatus(s)}
                        disabled={s.is_default}
                        data-testid={`toggle-status-${s.site_id}`}
                      >
                        {s.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                  {expanded[s.site_id] && (
                    <tr className="bg-stone-50/60" data-testid={`bootstrap-panel-${s.site_id}`}>
                      <td colSpan={8} className="p-4 space-y-4">
                        <BootstrapChecklist
                          siteId={s.site_id}
                          loading={bootstrapLoading[s.site_id]}
                          data={bootstrap[s.site_id]}
                          onRefresh={() => loadBootstrap(s.site_id)}
                          onNavigate={(path) => navigate(path)}
                        />
                        {scope === 'all' && (
                          <div className="border-t border-stone-200 pt-3">
                            <DispatchSectionsPanel
                              siteId={s.site_id}
                              isSalem={s.site_id === 'salem' || s.is_default}
                            />
                          </div>
                        )}
                        {scope === 'all' && (
                          <div className="border-t border-stone-200 pt-3">
                            <SiteAdminGrants siteId={s.site_id} siteName={s.site_name} />
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex gap-2">
          <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Multi-site Phase 2 + 3 active.</strong> Every operator
            page is now site-aware via the <span className="font-mono">X-Site-Id</span> header
            (the dependency-injected DB resolver routes reads/writes to the active site's
            MongoDB). New sites are provisioned with Salem-equivalent indexes and{' '}
            <strong>start with 0 records</strong> in every collection — operators must upload
            their own item master / BOMs / designs / piece rates from this site's import pages.
            Bundle and document codes are auto-prefixed with the site's{' '}
            <span className="font-mono">code_prefix</span> (Salem keeps the legacy unprefixed format).
          </div>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-site-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" /> Create New Site
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Site name *</Label>
              <Input
                value={form.site_name}
                onChange={(e) => setForm({ ...form, site_name: e.target.value })}
                placeholder="e.g. Srivilliputhur"
                data-testid="form-site-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Site code (3 chars) *</Label>
                <Input
                  value={form.site_code}
                  onChange={(e) => setForm({ ...form, site_code: e.target.value.toUpperCase().slice(0, 3) })}
                  placeholder="SVPR"
                  className="font-mono"
                  data-testid="form-site-code"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Hash className="h-3 w-3" />Code prefix (2–5) *</Label>
                <Input
                  value={form.code_prefix}
                  onChange={(e) => setForm({ ...form, code_prefix: e.target.value.toUpperCase().slice(0, 5) })}
                  placeholder="SVPR"
                  className="font-mono"
                  data-testid="form-code-prefix"
                />
              </div>
            </div>
            <p className="text-[11px] text-stone-500 -mt-1">
              Bundle/document codes will be prefixed: e.g.{' '}
              <span className="font-mono">{form.code_prefix || 'SVPR'}-BUN000001/25-26</span>.
              Salem keeps the legacy unprefixed format (<span className="font-mono">BUN000001/25-26</span>).
              Each site's bundle counter starts at 0001.
            </p>
            <div>
              <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Srivilliputhur, Tamil Nadu"
                data-testid="form-location"
              />
            </div>
            <div className="border-t pt-3">
              <div className="text-xs font-semibold text-stone-700 mb-2 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Initial Site Admin (optional, can also be created later)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Admin name</Label>
                  <Input
                    value={form.initial_admin_name}
                    onChange={(e) => setForm({ ...form, initial_admin_name: e.target.value })}
                    placeholder="Manager name"
                    data-testid="form-admin-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Admin email</Label>
                  <Input
                    type="email"
                    value={form.initial_admin_email}
                    onChange={(e) => setForm({ ...form, initial_admin_email: e.target.value })}
                    placeholder="manager@..."
                    data-testid="form-admin-email"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
            <Button
              onClick={onCreate}
              disabled={creating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="form-submit-btn"
            >
              {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Provisioning…</> : 'Create Site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// BootstrapChecklist — shows live row counts of master collections in the
// active site's MongoDB DB, so a supervisor can instantly see whether a
// freshly-provisioned site is "ready to operate" or still has empty masters.
// ---------------------------------------------------------------------------
function BootstrapChecklist({ siteId, loading, data, onRefresh, onNavigate }) {
  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-stone-500 text-sm py-3">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reading {siteId} master collections…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-sm text-stone-500 py-3">
        No bootstrap data yet.{' '}
        <button onClick={onRefresh} className="text-emerald-600 underline" data-testid={`refresh-bootstrap-${siteId}`}>Retry</button>
      </div>
    );
  }
  const ready = data.summary?.ready_to_operate;
  return (
    <div className="space-y-3" data-testid={`bootstrap-${siteId}`}>
      <div className="flex items-center gap-2 text-xs">
        <Building2 className="h-4 w-4 text-stone-500" />
        <span className="font-semibold text-stone-700">Site bootstrap checklist</span>
        <Badge
          className={
            ready
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : 'bg-amber-100 text-amber-700 border-amber-200'
          }
          data-testid={`ready-badge-${siteId}`}
        >
          {ready
            ? `Ready · ${data.summary.required_filled}/${data.summary.required_total} required collections filled`
            : `Not ready · ${data.summary.required_filled}/${data.summary.required_total} required collections filled`}
        </Badge>
        <button
          onClick={onRefresh}
          className="ml-auto text-[11px] text-stone-500 hover:text-stone-700"
          data-testid={`refresh-bootstrap-${siteId}`}
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {(data.tiles || []).map((t) => {
          const Icon = t.filled
            ? CheckCircle2
            : (t.importance === 'required' ? AlertCircle : Circle);
          const tone = t.filled
            ? 'border-emerald-200 bg-emerald-50/40'
            : (t.importance === 'required'
                ? 'border-rose-200 bg-rose-50/40'
                : 'border-stone-200 bg-white');
          const iconClr = t.filled
            ? 'text-emerald-600'
            : (t.importance === 'required' ? 'text-rose-600' : 'text-stone-400');
          return (
            <div
              key={t.key}
              className={`flex items-start gap-2 rounded-md border ${tone} p-2.5`}
              data-testid={`tile-${siteId}-${t.key}`}
            >
              <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconClr}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-sm font-semibold text-stone-900 truncate">{t.label}</div>
                  <Badge
                    variant="outline"
                    className={
                      t.importance === 'required'
                        ? 'text-[10px] border-rose-200 text-rose-700'
                        : t.importance === 'recommended'
                          ? 'text-[10px] border-amber-200 text-amber-700'
                          : 'text-[10px] border-stone-200 text-stone-500'
                    }
                  >
                    {t.importance}
                  </Badge>
                </div>
                <div className="text-xs text-stone-500 font-mono mt-0.5">
                  {t.key} · <span className="text-stone-700 font-semibold">{t.count.toLocaleString()}</span> rows
                </div>
                {!t.filled && (
                  <button
                    onClick={() => onNavigate(t.import_path)}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 hover:underline"
                    data-testid={`upload-${siteId}-${t.key}`}
                  >
                    Upload data <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!ready && (
        <p className="text-[11px] text-stone-500 italic">
          New sites start empty (per system policy). Upload required masters before operators
          can run any flow on this site.
        </p>
      )}
    </div>
  );
}
